import React, { useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import { useParams, useNavigate } from "react-router-dom";
import LoadingWidget from "../../widgets/LoadingWidget";
import {
  Alert,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { getErrorResponse } from "../../utils/util";
import SocialContainer from "./SocialContainer";
import {
  FaEye,
  FaEyeSlash,
  FaLock,
  FaCheckCircle,
  FaCheck,
} from "react-icons/fa";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const SetCustomerPassword = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApiEp(
    `/api/check-email-verification?id=${id}`
  );

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        "Password must contain uppercase, lowercase, and numbers";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      // const response = await callApi({
      //   url: "/api/set-customer-password",
      //   method: "POST",
      //   data: {
      //     verificationId: id,
      //     password: formData.password,
      //   },
      // });

      // if (response.success) {
      //   // Password set successfully
      //   navigate("/login", {
      //     state: {
      //       message: "Password set successfully! You can now login.",
      //       severity: "success",
      //     },
      //   });
      // } else {
      //   setSubmitError(response.message || "Failed to set password");
      // }
      const response = await axios.post(`${API_ENDPOINT}/api/set-password`, {
        verificationId: id,
        password: formData.password,
      });
      const responseD = response.data;
      toast.success(responseD.message);
      if (responseD?.onboard) {
        //onboard user
        navigate(`/link-phonenumber/${responseD.onBoardToken}`);
      } else {
        navigate(`/login`);
      }
    } catch (error) {
      setSubmitError(getErrorResponse(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  if (isLoading) {
    return <LoadingWidget label="Checking link...." />;
  }

  if (isError) {
    return (
      <SocialContainer>
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorResponse(error)}
        </Alert>
      </SocialContainer>
    );
  }

  return (
    <SocialContainer>
      <Box sx={{ mt: 3, textAlign: "center", maxWidth: 400, margin: "0 auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <FaLock style={{ marginRight: 10, color: "#1976d2" }} />
          <Typography variant="h6" color="primary" fontWeight="600">
            Set Your Password
          </Typography>
        </Box>

        {/* Success Icon */}
        <Box sx={{ mb: 3 }}>
          <FaCheckCircle
            style={{ fontSize: 64, color: "#4CAF50", marginBottom: 2 }}
          />
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
            Hello {data?.user?.name || "there"}, create a secure password for
            your account
          </Typography>
        </Box>

        {/* Error Alert */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {/* Password Requirements */}
        <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
          <Typography variant="caption">
            Password must contain:
            <br />• At least 8 characters
            <br />• Uppercase and lowercase letters
            <br />• At least one number
          </Typography>
        </Alert>

        {/* Password Field */}
        <TextField
          fullWidth
          type={showPassword ? "text" : "password"}
          label="Password"
          value={formData.password}
          onChange={(e) => handleInputChange("password", e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!errors.password}
          helperText={errors.password}
          sx={{ mb: 2 }}
          required
          variant="outlined"
          disabled={isSubmitting}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={isSubmitting}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Confirm Password Field */}
        <TextField
          fullWidth
          type={showConfirmPassword ? "text" : "password"}
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          sx={{ mb: 3 }}
          required
          variant="outlined"
          disabled={isSubmitting}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  disabled={isSubmitting}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Submit Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
            },
            py: 1.5,
            borderRadius: 2,
            fontWeight: "600",
            mb: 2,
          }}
          startIcon={isSubmitting ? <LoadingWidget size={20} /> : <FaCheck />}
        >
          {isSubmitting ? "Setting Password..." : "Set Password"}
        </Button>

        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block" }}
        >
          Your password is encrypted and securely stored
        </Typography>
      </Box>
    </SocialContainer>
  );
};

export default SetCustomerPassword;
